import axios from 'axios'
import { ServiceError } from '@/utils/apiError'
import type { PinConfidence } from './PlacesService'

/** Precisão do ponto devolvido pelo Geocoding (campo `location_type` do Google). */
export type GeocodeLocationType =
  | 'ROOFTOP'
  | 'RANGE_INTERPOLATED'
  | 'GEOMETRIC_CENTER'
  | 'APPROXIMATE'

export type GeocodeResult = {
  lat: number
  lng: number
  formattedAddress: string
  locationType: GeocodeLocationType
  /** Só `ROOFTOP` cai no prédio; o resto é interpolação/centroide. */
  confidence: PinConfidence
}

/**
 * Mensagens do Geocoding (serviço externo). A falha não bloqueia o pino: as duas
 * apontam os planos B do passo 1 (buscar pelo nome, colar o link ou arrastar o
 * marcador), mas distinguem "o Google não achou este endereço" de "o Google não
 * respondeu".
 */
export const GEOCODE_NOT_FOUND_MESSAGE =
  'Endereço não encontrado no mapa. Confira o endereço do cadastro ou cole o link do Google Maps.'
export const GEOCODE_UNAVAILABLE_MESSAGE =
  'Não foi possível localizar o endereço no mapa agora. Tente novamente ou cole o link do Google Maps.'
export const SHORT_URL_UNRESOLVED_MESSAGE =
  'Não foi possível resolver o link curto. Use o link completo do Google Maps.'

type GoogleGeocodeResponse = {
  status: string
  results: Array<{
    geometry: { location: { lat: number; lng: number }; location_type: string }
    formatted_address: string
  }>
}

/**
 * Geocoding via Google Maps API.
 *
 * NÃO estende `Service`: a base prefixa a baseURL da API interna e injeta os
 * interceptors de auth, enquanto este service fala com `maps.googleapis.com`.
 * A key `VUE_APP_GMAPSAPIKEY` é pública por design (client-side, restrita por
 * referrer no console do Google).
 */
export default class GeocodingService {
  private readonly baseUrl = 'https://maps.googleapis.com/maps/api/geocode/json'

  private get apiKey(): string {
    return process.env.VUE_APP_GMAPSAPIKEY ?? ''
  }

  /**
   * Geocodifica um endereço textual. Lança `ServiceError` com mensagem pt-BR
   * (Pattern A): `ZERO_RESULTS` (ou `OK` sem resultado) → endereço não
   * encontrado; rede, timeout, cota (`OVER_QUERY_LIMIT`), chave recusada
   * (`REQUEST_DENIED`) e demais status → serviço indisponível.
   */
  async geocodeAddress(address: string): Promise<GeocodeResult> {
    const url = `${this.baseUrl}?address=${encodeURIComponent(address)}&key=${this.apiKey}`

    let response
    try {
      response = await axios.get<GoogleGeocodeResponse>(url)
    } catch (e) {
      throw new ServiceError(GEOCODE_UNAVAILABLE_MESSAGE, e)
    }

    const status = response.data?.status
    const result = response.data?.results?.[0]
    if (status === 'ZERO_RESULTS' || (status === 'OK' && !result)) {
      throw new ServiceError(GEOCODE_NOT_FOUND_MESSAGE, response.data)
    }
    if (status !== 'OK' || !result) {
      throw new ServiceError(GEOCODE_UNAVAILABLE_MESSAGE, response.data)
    }

    const locationType = result.geometry.location_type as GeocodeLocationType
    return {
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng,
      formattedAddress: result.formatted_address,
      locationType,
      confidence: locationType === 'ROOFTOP' ? 'BUILDING' : 'APPROXIMATE'
    }
  }

  /**
   * Busca o endereço formatado de um par de coordenadas.
   * Falha é tolerada (Pattern B): retorna `null` e loga warning.
   */
  async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    const url = `${this.baseUrl}?latlng=${lat},${lng}&key=${this.apiKey}`
    try {
      const response = await axios.get<GoogleGeocodeResponse>(url)
      if (response.data.status === 'OK') {
        return response.data.results[0].formatted_address
      }
      return null
    } catch (e) {
      // fallback intencional: o endereço textual só descreve o ponto já aplicado a
      // partir do link — sem ele o pino segue válido e visível no mapa, e o usuário
      // confirma a posição pela vista de satélite antes de salvar.
      console.warn('Reverse geocode failed', e)
      return null
    }
  }

  /** Resolve um link curto do Maps (maps.app.goo.gl) seguindo os redirects. */
  async resolveShortUrl(shortUrl: string): Promise<string> {
    try {
      const response = await axios.get(shortUrl, { maxRedirects: 5 })
      return response.request?.responseURL || shortUrl
    } catch (e) {
      throw new ServiceError(SHORT_URL_UNRESOLVED_MESSAGE, e)
    }
  }
}
